import React, { useEffect, useState } from "react";
import { Wand } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";
import { Stepper } from "@/components/wizard/Stepper";
import { StepImport, StepRoughCut } from "@/components/wizard/StepEarly";
import { StepReview } from "@/components/wizard/StepReview";
import { StepDeepAI, StepFinal } from "@/components/wizard/StepLate";

export default function Wizard() {
  const { projectId, project } = useApp();
  const { data: w, setData } = usePoll(() => (projectId ? api.wizard(projectId) : null), 5000, [projectId]);
  const [view, setView] = useState(null);
  useEffect(() => { if (w && !view) setView(w.current); }, [w, view]);
  if (!projectId) return <NoProject title="The Edit Wizard needs a project" />;
  if (!w) return <div className="p-8 text-[#94A3B8]">Loading…</div>;

  const call = async (fn, next) => {
    try { const d = await fn(); if (d?.steps) setData(d); if (next) setView(next); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const retry = () => call(() => api.wizardRetry(projectId));
  const active = view || w.current;

  return (
    <div className="p-8 space-y-5 fade-up" data-testid="wizard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-4xl font-bold flex items-center gap-3"><Wand className="text-[#E6B04E]" size={30} />Edit Wizard</h1>
          <p className="text-[#94A3B8] text-sm mt-1">{project?.name}: see your edit early, then deep-analyse only the clips that make the cut.</p>
        </div>
        {w.auto_production === "running" && <span className="metric-label text-[#fbbf24]" data-testid="wizard-autoprod-warning">Auto Production is still running. Starting the rough cut will stop it.</span>}
      </div>
      <Stepper order={w.order} steps={w.steps} active={active} onSelect={setView} />
      <div className="studio-card p-6">
        {active === "import" && <StepImport w={w} onNext={() => setView("rough_cut")} />}
        {active === "rough_cut" && <StepRoughCut w={w} onRetry={retry} onNext={() => setView("review")}
          onStart={(m) => call(() => api.wizardRoughCut(projectId, m))} />}
        {active === "review" && <StepReview pid={projectId} w={w} onNext={() => call(() => api.wizardDeepAI(projectId), "deep_ai")} />}
        {active === "deep_ai" && <StepDeepAI w={w} onRetry={retry} onNext={() => setView("final")}
          onStart={() => call(() => api.wizardDeepAI(projectId))} />}
        {active === "final" && <StepFinal pid={projectId} w={w} onRetry={retry} onStart={() => call(() => api.wizardFinal(projectId))} />}
      </div>
    </div>
  );
}
